import { useEffect } from "react";
import { createPortal } from "react-dom";
import { printedLabel, terminlisteMeta, terminlisteRows } from "@/components/admin/calendar/terminlisteRows";

/**
 * Print-only rendering of the terminliste exactly as filtered and sorted on screen (scope, room, search).
 * Mounted into <body>; `body.printing` hides the app behind it (see index.css) and the browser's print dialog opens.
 * Uses div (not header/footer) so the global print rule hiding page chrome does not hide the sheet's own heading.
 */
export const PrintTerminliste = ({ entries = [], scopeLabel, filterLabels = [], today, roomName, onDone }) => {
  useEffect(() => {
    document.body.classList.add("printing");
    const t = setTimeout(() => window.print(), 150);
    const done = () => onDone?.();
    window.addEventListener("afterprint", done);
    return () => { clearTimeout(t); window.removeEventListener("afterprint", done); document.body.classList.remove("printing"); };
  }, [onDone]);

  const rows = terminlisteRows(entries, { today, roomName });

  return createPortal(
    <div className="print-root" data-testid="print-terminliste">
      <div className="print-head">
        <div>
          <p className="print-eyebrow">Halsnøy Kulturhus SA</p>
          <h1>Terminliste{scopeLabel ? ` · ${scopeLabel}` : ""}</h1>
        </div>
        <p className="print-meta" data-testid="print-terminliste-meta">
          {terminlisteMeta({ scopeLabel, filterLabels, count: entries.length })}<br />Skrive ut {printedLabel()}
        </p>
      </div>

      <table className="print-table">
        <thead><tr><th>Dato</th><th>Kl.</th><th>Arrangement</th><th>Rom</th><th>Kontakt</th><th>Synleg</th></tr></thead>
        <tbody>
          {entries.length === 0 && <tr><td colSpan={6} className="muted" data-testid="print-terminliste-empty">Ingen oppføringar i dette utvalet.</td></tr>}
          {rows.map((r) => r.divider ? (
            <tr key={r.id} className="print-divider"><td colSpan={6}>{r.divider}</td></tr>
          ) : (
            <tr key={r.id}>
              <td className="nowrap">{r.date}</td>
              <td className="nowrap">{r.time || "–"}</td>
              <td><strong>{r.title}</strong>{r.subtitle ? <span className="muted"> · {r.subtitle}</span> : null}{r.notes ? <div className="muted small">{r.notes}</div> : null}</td>
              <td>{r.rooms}</td>
              <td>{r.contact || "–"}</td>
              <td className="nowrap">{r.visibility}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="print-foot">Skrive ut frå admin-panelet · halsnoykulturhus.no</div>
    </div>,
    document.body,
  );
};
